// ─────────────────────────────────────────────────────────────────────────────
// Messages — shared logic (DESIGN-CONCEPT-MOBILE §1.1).
//
// `Messages.web.jsx` and `Messages.mobile.jsx` are two independent render trees
// that must never *disagree* about what a conversation row is, how the list is
// ordered, what a row's time label means, or how an unread count is capped.
// All of that non-visual truth lives here: pure functions and translation KEYS
// only — no markup, no hooks, no literal copy (a plain module cannot call the
// translator; helpers that need one take it as an argument, the
// `exportLinesCsv(rows, label, t)` precedent).
// ─────────────────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

// Start of the calendar day an instant falls in, in the device's local time.
// Doctors and their clinics run on Iran local time; the row-time rule is about
// "what the doctor's clock calls today", which is exactly what local time is.
const dayStart = (ts) => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// ── Conversation list ─────────────────────────────────────────────────────────

/** Newest activity first. Pure — returns a new array. */
export const sortConversations = (rows) =>
  [...rows].sort((a, b) => (b.lastActivityTs || 0) - (a.lastActivityTs || 0));

/**
 * BFF `chatConversations` ∪ matrix `getRooms()`, keyed by roomId.
 *
 * The BFF knows WHO a room is with (peer name/type — it is the directory); the
 * matrix client knows what is HAPPENING in it (unread, last event, activity).
 * A room the matrix client holds that the BFF page missed still renders — with
 * no peer identity, which the surfaces name through `messages.unknownContact` —
 * because a conversation with unread messages must never silently vanish from
 * the list.
 *
 * `createdAt` (epoch-ms-as-a-string, per the BFF contract) seeds lastActivityTs
 * for rooms the matrix client has not synced yet, so a fresh room still sorts.
 */
export function mergeConversations(bffConversations = [], matrixRooms = []) {
  const byRoom = new Map(matrixRooms.map((r) => [r.roomId, r]));
  const rows = bffConversations.map((c) => {
    const live = byRoom.get(c.roomId);
    byRoom.delete(c.roomId);
    return {
      roomId: c.roomId,
      peerDisplayName: c.peerDisplayName || null,
      peerType: c.peerType || null,
      peerSubjectId: c.peerSubjectId || null,
      role: null, // ChatConversation carries no role; roleLabelKey falls back on peerType
      reachable: true,
      unread: live?.unread || 0,
      lastEvent: live?.lastEvent || null,
      lastActivityTs: live?.lastActivityTs || Number(c.createdAt) || 0,
      started: true,
    };
  });
  for (const [roomId, live] of byRoom) {
    rows.push({
      roomId,
      peerDisplayName: null,
      peerType: null,
      peerSubjectId: null,
      role: null,
      reachable: true,
      unread: live.unread || 0,
      lastEvent: live.lastEvent || null,
      lastActivityTs: live.lastActivityTs || 0,
      started: true,
    });
  }
  return sortConversations(rows);
}

/** Case-insensitive name match. An empty query matches everything. */
export function matchesSearch(row, query) {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return true;
  return (row.peerDisplayName || '').toLowerCase().includes(needle);
}

/**
 * Contacts that match the search but have no conversation row yet — the
 * "not started" entries appended under the list while searching. Only while
 * searching: the resting list is conversations, not the whole directory.
 */
export function contactStubs(contacts = [], conversations = [], query = '') {
  if (!(query || '').trim()) return [];
  const haveRoom = new Set(conversations.map((c) => c.roomId).filter(Boolean));
  return contacts
    .filter((c) => !c.roomId || !haveRoom.has(c.roomId))
    .map((c) => ({
      key: `${c.subjectType}:${c.subjectId}`,
      roomId: c.roomId || null,
      peerDisplayName: c.displayName || null,
      peerType: c.subjectType || null,
      peerSubjectId: c.subjectId,
      role: c.role || null,
      // False = the patient has no account yet. The room is still writable —
      // the surfaces show the hint, they never block the send.
      reachable: c.reachable !== false,
      unread: 0,
      lastEvent: null,
      lastActivityTs: 0,
      started: false,
    }))
    .filter((row) => matchesSearch(row, query));
}

// ── Row time (ONE rule for both surfaces) ────────────────────────────────────

/** 'time' (today) | 'yesterday' | 'date' (anything older). */
export function rowTimeKind(ts, now = Date.now()) {
  const today = dayStart(now);
  if (ts >= today) return 'time';
  if (ts >= today - DAY_MS) return 'yesterday';
  return 'date';
}

/**
 * The conversation-row time label: today → clock time, yesterday → the
 * translated «دیروز», older → the (Jalali-in-fa) date. Takes the translator
 * bundle as an argument so it stays a pure module-level fact.
 */
export function conversationTimeLabel(ts, { t, formatTime, formatDate }, now = Date.now()) {
  if (!ts) return '';
  switch (rowTimeKind(ts, now)) {
    case 'time': return formatTime(ts);
    case 'yesterday': return t('messages.yesterday');
    default: return formatDate(ts, 'short');
  }
}

// ── Unread ───────────────────────────────────────────────────────────────────

export const UNREAD_CAP = 9;

/**
 * Display form of an unread count: localised digits, capped at «۹+». Empty
 * string for zero — a zero badge is a badge that should not exist.
 */
export function formatUnread(count, formatNumber) {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  if (n > UNREAD_CAP) return `${formatNumber(UNREAD_CAP)}+`;
  return formatNumber(n);
}

/** Rows with anything unread — the «خوانده‌نشده» filter. */
export const filterUnread = (rows) => rows.filter((r) => (r.unread || 0) > 0);

// ── Thread ───────────────────────────────────────────────────────────────────

/**
 * A timeline → consecutive same-day groups: [{ dayTs, messages }]. The surfaces
 * format `dayTs` into the divider label (Jalali in fa) — the grouping itself is
 * calendar-agnostic.
 */
export function groupTimelineByDay(timeline = []) {
  const groups = [];
  for (const msg of timeline) {
    const day = dayStart(msg.ts || 0);
    const last = groups[groups.length - 1];
    if (last && last.dayTs === day) last.messages.push(msg);
    else groups.push({ dayTs: day, messages: [msg] });
  }
  return groups;
}

// ── Vocabulary keys ──────────────────────────────────────────────────────────

// ChatContact.role is authoritative when present; a ChatConversation only says
// patient-or-user, so a user peer without a role falls back to «کارمند» — the
// safe generic for a clinic colleague.
export function roleLabelKey(row) {
  if (row?.role === 'doctor') return 'messages.roles.doctor';
  if (row?.role === 'staff') return 'messages.roles.staff';
  if (row?.role === 'patient') return 'messages.roles.patient';
  return String(row?.peerType || '').includes('PATIENT')
    ? 'messages.roles.patient'
    : 'messages.roles.staff';
}

/**
 * `readOnlyReason` is a server-side code/sentence; the notice the doctor reads
 * is ours. Known reasons get specific copy, anything else the generic one —
 * never render the raw wire value.
 */
export function readOnlyNoticeKey(reason) {
  const r = String(reason || '').toUpperCase();
  if (r.includes('SUBSCRIPTION') || r.includes('LAPSE')) return 'messages.readOnly.subscription';
  return 'messages.readOnly.generic';
}
